import { isDateString, mapObject } from "common";
import dayjs from "dayjs";
import Pusher, { Channel } from "pusher-js";
import {
    Event,
    Experience,
    FeatureRole,
    Package,
    PurchaseRuleLink,
    PurchaseRuleLinkEntityObject,
    PurchaseRuleLinkTag,
    Seller,
    User,
    XChatDisabledPreferenceValue,
} from "types";
import { queryClient } from "../api/queryClient";
import { xolaUri } from "../api/xolaUri";
import { config } from "../config";
import { broadcastEventToEmbeddedApps } from "../embeddedApps/hostRpc";
import { hasFeature } from "../models/Seller";
import { loadSnippet, registerSnippet } from "./index";
import { getCredentialHeaders } from "@/api/credentialHeaders";
import { updatePurchaseAndTimeslotsCache, updateTimeslotsCollection } from "@/components/Dashboard/helper";
import { getCollection } from "@/utils/getCollection";

let pusherInstance: Pusher | null = null;

const initializePusher = (seller: Seller): Pusher | null => {
    if (!hasFeature(seller, FeatureRole.REALTIME_UPDATES) || !config.pusher?.key) {
        return null;
    }

    if (pusherInstance) {
        return pusherInstance;
    }

    pusherInstance = new Pusher(config.pusher.key, {
        authEndpoint: `${xolaUri}/api/pusher/auth?seller=${seller.id}`,
        auth: { headers: getCredentialHeaders() },
        cluster: config.pusher.cluster ? config.pusher.cluster : undefined,
    });

    return pusherInstance;
};

const getPusherChannel = (seller: Seller): Channel | null => {
    const pusher = initializePusher(seller);
    if (!pusher) {
        return null;
    }

    const channelName = `private-${seller.id}`;
    return pusher.subscribe(channelName);
};

registerSnippet("Pusher", !!config.pusher.key, (user: User, seller: Seller) => {
    const channel = getPusherChannel(seller);
    if (!channel) {
        return;
    }

    const chunkedEvents: any = {};

    const bindWithChunking = (channel: Channel, event: string, callback: (data: any) => void) => {
        // Unbind any previous bindings, sometimes I see the event being calld twice for one message
        channel.unbind(event);
        channel.unbind(`chunked-${event}`);

        channel.bind(event, (data: any) => {
            // Convert date strings to `dayjs` like we do in interceptors
            callback(mapObject(data, (value) => (isDateString(value) ? dayjs(value).tz() : value)));
        });

        channel.bind(`chunked-${event}`, (data: any) => {
            if (chunkedEvents[data.id] === undefined) {
                chunkedEvents[data.id] = { chunks: [], receivedFinal: false };
            }

            const e = chunkedEvents[data.id];
            e.chunks[data.index] = data.chunk;

            if (data.end) e.receivedFinal = true;

            if (e.receivedFinal && e.chunks.length === Object.keys(e.chunks).length) {
                const data = JSON.parse(e.chunks.join(""));
                // Convert date strings to `dayjs` like we do in interceptors
                callback(mapObject(data, (value) => (isDateString(value) ? dayjs(value).tz() : value)));
                // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                delete chunkedEvents[data.id];
            }
        });
    };

    bindWithChunking(channel, "experience", (experience: Experience) => {
        queryClient.invalidateQueries(["experiences", "collection"]);
        queryClient.invalidateQueries(["products", "collection"]);
        queryClient.invalidateQueries(["experiences", experience.id]);
        queryClient.invalidateQueries(["products", experience.id]);
        // In experience we generate purchase rules after the experience is saved, pusher triggers the event when experience is persisted.
        // on slow network it can happen that we fetched puchase rules before it was updated, for this case added 1 sec timeout
        setTimeout(() => {
            const purchaseRuleLinks = getCollection<PurchaseRuleLink>("purchaseRuleLinks");
            for (const purchaseRuleLink of purchaseRuleLinks) {
                const experiencePurchaseRuleLink = purchaseRuleLink.entities.find(({ id }) => id === experience.id);
                if (experiencePurchaseRuleLink) {
                    queryClient.invalidateQueries([
                        "purchaseRuleLinks",
                        "collection",
                        {
                            "entities.id": experience.id,
                            "tags._id": PurchaseRuleLinkTag.PRICING,
                        },
                    ]);
                }
            }
        }, 1000);
        broadcastEventToEmbeddedApps("data.experience", { id: experience.id });
    });

    bindWithChunking(channel, "purchase-id", async (purchaseFromPusher: { id: ID }) => {
        updatePurchaseAndTimeslotsCache(purchaseFromPusher);
        broadcastEventToEmbeddedApps("data.purchase", { id: purchaseFromPusher.id });
    });

    bindWithChunking(channel, "package", (pkg: Package) => {
        queryClient.invalidateQueries(["packages", pkg.id]);
        const purchaseRuleLinks = getCollection<PurchaseRuleLink>("purchaseRuleLinks");
        for (const purchaseRuleLink of purchaseRuleLinks) {
            const pkgPurchaseRuleLink = purchaseRuleLink.entities.find(({ id }) => id === pkg.id);
            if (pkgPurchaseRuleLink) {
                queryClient.invalidateQueries([
                    "purchaseRuleLinks",
                    "collection",
                    {
                        "entities.id": pkg.id,
                        "entities.object": PurchaseRuleLinkEntityObject.PACKAGE,
                    },
                ]);
            }
        }
        broadcastEventToEmbeddedApps("data.package", { id: pkg.id });
    });

    bindWithChunking(channel, "event", (event: Event) => {
        queryClient.invalidateQueries(["events", "collection"]);
        queryClient.invalidateQueries(["events", event.id]);

        updateTimeslotsCollection(event);
        broadcastEventToEmbeddedApps("data.event", { id: event.id });
    });

    channel.bind("xchat_disabled-updated", async (isXchatEnabled: XChatDisabledPreferenceValue) => {
        loadSnippet("XChat", user, seller, isXchatEnabled.value);
    });
});
